import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Order, Conversation, EventTicket, SUBSCRIPTION_PLANS } from '../types';
import { ordersApi, messagesApi, ticketsApi, walletApi } from '../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [unreadMessages, setUnreadMessages] = useState<Conversation[]>([]);
  const [upcomingTickets, setUpcomingTickets] = useState<EventTicket[]>([]);
  const [, setWalletBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setError] = useState<string | null>(null);

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [ordersRes, messagesRes, ticketsRes, walletRes] = await Promise.all([
        ordersApi.getMyOrders(1, 5),
        messagesApi.getConversations(1),
        ticketsApi.getMyTickets(),
        walletApi.getBalance(),
      ]);

      const orderList = ordersRes?.data ?? ordersRes?.orders ?? [];
      setRecentOrders(Array.isArray(orderList) ? orderList.slice(0, 5) : []);
      if (messagesRes.success) {
        const unread = (messagesRes.data || []).filter((c: Conversation) => c.unreadCount > 0);
        setUnreadMessages(unread);
      }
      if (ticketsRes.success) {
        const now = new Date();
        const list = ticketsRes.data ?? ticketsRes.tickets ?? [];
        const upcoming = (Array.isArray(list) ? list : []).filter((t: any) =>
          new Date(t.event?.date ?? t.eventDate ?? 0) > now
        );
        setUpcomingTickets(upcoming.slice(0, 5));
      }
      if (walletRes.success) {
        setWalletBalance(walletRes.data?.balance || 0);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // NOTE: Demo data removed - now using live API data

  const currentPlan = user ? SUBSCRIPTION_PLANS[user.subscription] : SUBSCRIPTION_PLANS.free;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return '#28a745';
      case 'shipped': return '#17a2b8';
      case 'processing': return '#ffc107';
      case 'pending': return '#6c757d';
      case 'cancelled': return '#dc3545';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <span style={{ fontSize: '48px' }}>🥭</span>
        <p style={{ color: '#666', marginTop: '20px' }}>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Header */}
      <div style={{
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
        borderRadius: '16px',
        padding: '30px',
        marginBottom: '25px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600' }}>
            Welcome back, {user?.firstName}! 👋
          </h1>
          <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
            Your personal Mango dashboard - track orders, manage subscriptions, and earn rewards.
          </p>
          <div style={{ display: 'flex', gap: '15px', marginTop: '15px', flexWrap: 'wrap' }}>
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '13px'
            }}>
              🥭 {currentPlan.name}
            </span>
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '13px'
            }} title={user?.mangoId ? undefined : 'Link your account from Staff → Add by Mango Personal in your business dashboard to get a Mango ID'}>
              ID: {user?.mangoId || '(not linked)'}
            </span>
            <button type="button" onClick={() => loadDashboardData(true)} disabled={refreshing} style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '20px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        
        {/* Wallet Quick View */}
        <div style={{
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '12px',
          padding: '20px 25px',
          textAlign: 'center',
          minWidth: '180px'
        }}>
          <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>Mango Credits</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '32px', fontWeight: '700' }}>
            🥭 {user?.wallet.mangoCredits.toFixed(0)}
          </p>
          {user?.wallet.pendingCredits && user.wallet.pendingCredits > 0 && (
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
              +{user.wallet.pendingCredits.toFixed(0)} pending
            </p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '15px',
        marginBottom: '30px'
      }}>
        {[
          { label: 'View Orders', icon: '📦', path: '/orders', color: '#667eea' },
          { label: 'Subscriptions', icon: '🔄', path: '/subscriptions', color: '#ff6b35' },
          { label: 'Business CRM', icon: '🧭', path: '/business-crm', color: '#0ea5e9' },
          { label: 'My Wallet', icon: '💰', path: '/wallet', color: '#28a745' },
          { label: 'Messages', icon: '💬', path: '/messages', badge: unreadMessages.reduce((sum, c) => sum + c.unreadCount, 0) },
          { label: 'My Tickets', icon: '🎫', path: '/tickets', color: '#f093fb' },
          { label: 'Wishlist', icon: '❤️', path: '/wishlist', color: '#ff6b6b' },
          { label: 'Discover', icon: '🔍', path: '/businesses', color: '#17a2b8' },
        ].map((action) => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            style={{
              background: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '20px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
            }}
          >
            <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>{action.icon}</span>
            <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>{action.label}</span>
            {action.badge && action.badge > 0 && (
              <span style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#ff4444',
                color: 'white',
                fontSize: '11px',
                fontWeight: '600',
                padding: '3px 8px',
                borderRadius: '10px'
              }}>
                {action.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '25px'
      }}>
        {/* Recent Orders */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '25px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>📦 Recent Orders</h2>
            <button
              onClick={() => navigate('/orders')}
              style={{ background: 'none', border: 'none', color: '#ff6b35', cursor: 'pointer', fontSize: '13px' }}
            >
              View All →
            </button>
          </div>

          {recentOrders.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '30px' }}>No orders yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {recentOrders.map(order => (
                <div
                  key={order.id}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  style={{
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        {order.businessName ?? order.business?.name ?? 'Business'}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                        {order.orderNumber}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#888' }}>
                        {(order.items?.length ?? 0)} item{(order.items?.length ?? 0) !== 1 ? 's' : ''} • ${(order.total ?? 0).toFixed(2)}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      background: getStatusColor(order.status) + '20',
                      color: getStatusColor(order.status),
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {order.status ?? order.fulfillmentStatus ?? 'pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messages */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '25px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>💬 Messages</h2>
            <button
              onClick={() => navigate('/messages')}
              style={{ background: 'none', border: 'none', color: '#ff6b35', cursor: 'pointer', fontSize: '13px' }}
            >
              View All →
            </button>
          </div>

          {unreadMessages.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '30px' }}>No unread messages</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {unreadMessages.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => navigate(`/messages/${conv.id}`)}
                  style={{
                    padding: '15px',
                    background: '#fff3e0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    borderLeft: '4px solid #ff6b35'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        {conv.businessName}
                      </p>
                      <p style={{ 
                        margin: '6px 0 0 0', 
                        fontSize: '13px', 
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {conv.lastMessage}
                      </p>
                    </div>
                    {conv.unreadCount > 0 && (
                      <span style={{
                        background: '#ff6b35',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '10px',
                        marginLeft: '10px'
                      }}>
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Events */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '25px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>🎫 Upcoming Events</h2>
            <button
              onClick={() => navigate('/tickets')}
              style={{ background: 'none', border: 'none', color: '#ff6b35', cursor: 'pointer', fontSize: '13px' }}
            >
              View All →
            </button>
          </div>

          {upcomingTickets.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '30px' }}>No upcoming events</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {upcomingTickets.map(ticket => (
                <div
                  key={ticket.id}
                  style={{
                    padding: '15px',
                    background: 'linear-gradient(135deg, #667eea15 0%, #f093fb15 100%)',
                    borderRadius: '10px',
                    border: '1px solid #667eea30'
                  }}
                >
                  <p style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '15px' }}>
                    {ticket.event?.name ?? ticket.eventName ?? 'Event'}
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                    📅 {new Date(ticket.event?.date ?? ticket.eventDate ?? 0).toLocaleDateString('en-AU', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#888' }}>
                    📍 {ticket.event?.location ?? ticket.eventLocation ?? '-'}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                    <span style={{
                      padding: '4px 10px',
                      background: '#667eea',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {ticket.ticketType ?? 'Ticket'}
                    </span>
                    <span style={{
                      padding: '4px 10px',
                      background: '#28a74520',
                      color: '#28a745',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {ticket.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upgrade Prompt (for free users) */}
        {user?.subscription === 'free' && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '25px',
            color: 'white'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px' }}>🚀 Upgrade to Mango Social</h2>
            <p style={{ margin: '12px 0 0 0', opacity: 0.9, fontSize: '14px', lineHeight: 1.5 }}>
              Unlock the marketplace, create events, connect with other Mango users, and more!
            </p>
            <ul style={{ margin: '15px 0', paddingLeft: '20px', fontSize: '13px', lineHeight: 1.8 }}>
              <li>Sell up to 3 products</li>
              <li>Create personal events</li>
              <li>Message other customers</li>
              <li>Join & create groups</li>
            </ul>
            <button
              onClick={() => navigate('/upgrade')}
              style={{
                width: '100%',
                padding: '14px',
                background: 'white',
                color: '#764ba2',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Upgrade for $3/month →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
